/* The start chooser (0.3d, simplified in 0.3e): what a new gangsheet opens with. Two choices: DTF or
   UV DTF (big names, one short line, the starting prices), and how to begin (Auto Build, a blank sheet
   laid out by hand, or a gangsheet saved in this browser). "What's the difference?" folds out the
   longer explanation. Prices and sheet sizes come from the shop's settings (Admin → pricing).
   Opened with New gangsheet over an open gangsheet, it offers the way back to it. */

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { priceSheets, formatMoney, formatDims, type Transfer } from '@gsb/core';
import { api } from '../api';
import { navigate } from '../router';
import { useStore, startGangsheet, closeStart, firstVisit, openEmailFlow } from '../store/store';
import { Dialog } from '../ui/primitives';
import { Icon } from '../ui/icons';

/* one short line under each name, and the fold-out details (the names come from Admin → pricing) */
const SHORT: Record<Transfer, string> = {
  dtf: 'For clothing and fabrics.',
  uv: 'For cups, glass and other hard surfaces.'
};
const ABOUT: Record<Transfer, { on: string; how: string; not: string }> = {
  dtf: { on: 'T-shirts, hoodies, bags and other fabrics', how: 'With a heat press or a home iron', not: 'Cups, glass and other hard surfaces' },
  uv: { on: 'Cups, glass, metal, plastic and other hard, smooth surfaces', how: 'Rubbed on, no heat needed', not: 'Clothing and fabrics' }
};

export function StartDialog() {
  const cfg = useStore(s => s.config)!;
  const current = useStore(s => s.doc.transfer);
  const from = useStore(s => s.startFrom);
  const [t, setT] = useState<Transfer>(current);
  const [saved, setSaved] = useState<number | null>(null);
  const [diff, setDiff] = useState(false);
  const first = useMemo(() => firstVisit() && !from, []);
  useEffect(() => { api.list().then(r => setSaved(r.projects.length)).catch(() => setSaved(null)); }, []);
  // start on the chosen transfer type (the dialog would otherwise focus its close button)
  useEffect(() => { (document.querySelector('.st-type[aria-checked="true"]') as HTMLElement | null)?.focus({ preventScroll: true }); }, []);

  const tp = cfg.pricing.transfers;
  const facts = (k: Transfer) => {
    const bl = tp[k].byLength;
    const firstMetre = priceSheets([{ kind: 'roll', widthMm: bl.widthMm, lengthMm: bl.minLengthMm }], k, cfg.pricing as never).total;
    // the price per metre at the shortest length, as charged (the same figure as "1 m minimum: $55.00")
    const perMetre = firstMetre / (bl.minLengthMm / 1000);
    const from = tp[k].fixedSheets.length ? Math.min(...tp[k].fixedSheets.map(f => f.price)) : null;
    return { bl, firstMetre, perMetre, from, tiers: tp[k].tiers.length > 0 };
  };
  const f = facts(t);
  // "A4, A3, half metre or by the metre", cheapest first
  const lower = (n: string) => (/^[A-Z][a-z]/.test(n) ? n[0].toLowerCase() + n.slice(1) : n);
  const sheetNames = [...[...tp[t].fixedSheets].sort((a, b) => a.price - b.price).map(x => lower(x.name)), ...(f.bl.enabled ? ['by the metre'] : [])];
  const sheetsText = sheetNames.length > 1 ? sheetNames.slice(0, -1).join(', ') + ' or ' + sheetNames[sheetNames.length - 1] : sheetNames.join('');
  const startSize = formatDims(f.bl.widthMm, f.bl.minLengthMm, 'cm');
  const minM = f.bl.minLengthMm / 1000;
  const cheapest = [...tp[t].fixedSheets].sort((a, b) => a.price - b.price)[0];
  // the cheapest fixed sheet costs less than the shortest by-the-metre sheet: say so where it matters
  const autoCheaper = cheapest && cheapest.price < f.firstMetre ? cheapest : null;

  // arrow keys move between the two transfer types, like any radio group
  const keys = (e: KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const next: Transfer = t === 'dtf' ? 'uv' : 'dtf';
    setT(next);
    (document.querySelector('.st-type[data-t="' + next + '"]') as HTMLElement | null)?.focus();
  };
  const openSaved = () => { if (from) closeStart(); navigate('/my'); };

  return (
    <Dialog title={first ? 'Welcome to the Gangsheet Builder' : 'Start a gangsheet'} onClose={closeStart} className="start-dlg">
      <header className="st-head">
        {from && <button type="button" className="st-back link" onClick={closeStart}><Icon name="arrowLeft" />Back to “{from.name || 'your gangsheet'}”</button>}
        <span className="kicker">Printees Gangsheet Builder</span>
        <h2>{first ? 'Welcome! Let’s make your gangsheet' : 'Start a gangsheet'}</h2>
        <p className="muted">
          {first ? 'Two quick choices and you’re in.'
            : from ? '“' + (from.name || 'Your gangsheet') + '” is saved. Choose what you’re printing and how to begin.'
              : 'Choose what you’re printing and how you’d like to begin.'}
        </p>
      </header>

      <section className="st-sec" aria-labelledby="st-q1">
        <h3 id="st-q1" className="st-q"><span className="st-n">1</span>What are you printing?</h3>
        <div className="st-types" role="radiogroup" aria-labelledby="st-q1">
          {(['dtf', 'uv'] as const).map(k => {
            const x = facts(k);
            return (
              <button key={k} type="button" role="radio" aria-checked={t === k} tabIndex={t === k ? 0 : -1} data-t={k}
                className="st-type" onClick={() => setT(k)} onKeyDown={keys}>
                <span className="st-type-h">
                  <span className="st-dot" aria-hidden="true" />
                  <span className="st-type-name">{tp[k].label}</span>
                  <span className="st-check" aria-hidden="true"><Icon name="check" /></span>
                </span>
                <span className="st-type-sub">{SHORT[k]}</span>
                <span className="st-type-price num">
                  {x.from !== null && <span>From <b>{formatMoney(x.from)}</b> a sheet</span>}
                  {x.bl.enabled && <span><b>{formatMoney(x.perMetre)}</b> per metre</span>}
                </span>
              </button>
            );
          })}
        </div>
        <button type="button" className="st-diff" aria-expanded={diff} aria-controls="st-diff" onClick={() => setDiff(v => !v)}>
          <Icon name="chevRight" className={'chev' + (diff ? ' is-open' : '')} />What’s the difference?
        </button>
        {diff && (
          <div className="st-diffs" id="st-diff" role="region" aria-label="DTF and UV DTF compared">
            {(['dtf', 'uv'] as const).map(k => (
              <div key={k} className="st-diffcol" data-t={k}>
                <span className="st-pic" aria-hidden="true">{k === 'dtf' ? <ShirtPic /> : <CupPic />}</span>
                <div className="st-diffbody">
                  <b><span className="st-dot" />{tp[k].label}</b>
                  <dl>
                    <dt>Goes on</dt><dd>{ABOUT[k].on}</dd>
                    <dt>Applied</dt><dd>{ABOUT[k].how}</dd>
                    <dt>Not suitable for</dt><dd>{ABOUT[k].not}</dd>
                  </dl>
                </div>
              </div>
            ))}
            {f.tiers && f.bl.enabled && <p className="tiny muted st-diffnote">By the metre gets cheaper per metre on longer orders.</p>}
          </div>
        )}
      </section>

      <section className="st-sec" aria-labelledby="st-q2">
        <h3 id="st-q2" className="st-q"><span className="st-n">2</span>How would you like to start?</h3>
        <div className="st-opts">
          <button type="button" className="st-opt is-rec" data-start="auto" onClick={() => startGangsheet('auto', t)}>
            <span className="st-ico" aria-hidden="true"><Icon name="build" /></span>
            <span className="st-opt-body">
              <span className="st-opt-t"><b>Auto Build</b><em className="st-rec">Recommended</em></span>
              <span>Upload your designs. We fit them on the cheapest sheet: {sheetsText}.</span>
              {f.from !== null && <small className="num">Best price for most orders · from {formatMoney(f.from)}</small>}
            </span>
            <Icon name="chevRight" className="st-go" />
          </button>
          {f.bl.enabled && (
            <button type="button" className="st-opt" data-start="new" onClick={() => startGangsheet('new', t)}>
              <span className="st-ico" aria-hidden="true"><Icon name="plus" /></span>
              <span className="st-opt-body">
                <span className="st-opt-t"><b>Start new gangsheet</b></span>
                <span>A blank {startSize} sheet you arrange yourself.</span>
                <span className="st-min num"><Icon name="alert" />{minM} m minimum: {formatMoney(f.firstMetre)}, even for one small design.</span>
                {autoCheaper && <span className="st-opt-hint">Only a few small designs? Auto Build can use {/^[AEIOU]/.test(autoCheaper.name) ? 'an' : 'a'} {autoCheaper.name} for {formatMoney(autoCheaper.price)}.</span>}
              </span>
              <Icon name="chevRight" className="st-go" />
            </button>
          )}
          <button type="button" className="st-opt" data-start="open" disabled={saved === 0} onClick={openSaved}>
            <span className="st-ico" aria-hidden="true"><Icon name="sheets" /></span>
            <span className="st-opt-body">
              <span className="st-opt-t"><b>Open saved gangsheet</b></span>
              <span>{saved === null ? 'Carry on with a gangsheet saved in this browser.' : saved === 0 ? 'Nothing saved in this browser yet.' : saved + ' saved in this browser.'}</span>
            </span>
            <Icon name="chevRight" className="st-go" />
          </button>
          {cfg.email?.mode === 'on' && (
            <p className="st-find tiny muted">
              Saved a gangsheet to your email? <button type="button" className="link small" data-start="find" onClick={() => { closeStart(); openEmailFlow('find'); }}><Icon name="search" />Find My Gangsheets</button>
            </p>
          )}
        </div>
      </section>

      <p className="st-foot tiny muted">
        Switch type or sheet size any time.
        {cfg.pricing.taxInclusive ? ' Prices include ' + cfg.pricing.taxLabel + '.' : ''}
      </p>
    </Dialog>
  );
}

/* small drawings for "What's the difference?" (in the page, nothing to download) */
function ShirtPic() {
  return (
    <svg viewBox="0 0 64 64">
      <path d="M22 8l-12 6-6 14 9 4 3-6v32h32V26l3 6 9-4-6-14-12-6c-1 5-5 8-10 8s-9-3-10-8z" fill="#fff" stroke="#334155" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="32" cy="34" r="8" fill="#EA580C" />
      <path d="M27 35c1.5-3 3.2-4.5 5-4.5s3.5 1.5 5 4.5" fill="none" stroke="#FDBA74" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function CupPic() {
  return (
    <svg viewBox="0 0 64 64">
      <path d="M16 10h32l-4 46H20z" fill="#fff" stroke="#334155" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 10h36" stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      <rect x="23" y="24" width="18" height="18" rx="4" fill="#11C5D9" />
      <path d="M27 36l4-6 3 4 2-2 3 4" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
